/**
 * ONE CLOCK FOR EVERY SPINNER — the reset fix (Justin, 2026-09-26: "it keeps resetting awkwardly like it would rotate
 * then reset to original situation every second and it should be much more smooth"; design record D-71).
 *
 * The pill's clock rebuilds the pill's html every second (useGlobalTools.ts `refreshServerBackedProgressLabel`), and a
 * live-turn card's and a branch lane's clocks do the same (LiveTurnCardsOverlay.tsx). React replaces the element, and
 * a CSS animation on a new element starts again from its first frame: the ring snapped back every second, and on
 * every stage change. This module starts every `pendingSpin` animation at the same instant of the page's timeline
 * (`startTime = 0`), so a rebuilt spinner carries on exactly where the one it replaced was, and every spinner on screen
 * turns in step and lands on the logo together.
 *
 * - A MutationObserver on the body sees every insertion (an html repaint, a React mount, a portal). Its callback runs
 *   as a microtask after the task that changed the DOM and before the browser renders, so a new spinner's first
 *   painted frame is already in phase. It reads animations only for an added subtree that holds a spinner.
 * - An `animationstart` listener catches a spinner whose animation starts without an insertion (a pane shown again,
 *   reduced motion switched off): one frame late, then in step.
 * - A paused animation is left alone (a start time would un-pause it). A browser without `getAnimations` (jsdom, an
 *   old engine) spins as it always did. Installed once, at boot (src/index.tsx); imports only the markup's names.
 *
 * Pinned by tests/workingMark/phase.test.ts and, in a browser, scripts/check-mark-spinner.mjs (a pill repainted every
 * 100 ms never turns backwards; two spinners made 700 ms apart read the same angle).
 */
import { MARK_SPINNER_CLASS, MARK_SPIN_ANIMATION } from '../components/common/markSpinnerMarkup';

type Animated = Element & { getAnimations?: () => Animation[] };

/** Put this element's `pendingSpin` animation on the page's one clock. */
export function alignSpinnerAnimation(el: Element): void {
  const read = (el as Animated).getAnimations;
  if (typeof read !== 'function') return;
  for (const animation of read.call(el)) {
    if ((animation as Animation & { animationName?: string }).animationName !== MARK_SPIN_ANIMATION) continue;
    if (animation.playState === 'paused' || animation.startTime === 0) continue;
    try {
      animation.startTime = 0;
    } catch {
      // an engine that refuses a start time spins as it always did
    }
  }
}

/** Every spinner in `root` (and `root` itself), on the one clock. */
export function alignMarkSpinners(root: Element | Document | DocumentFragment | null | undefined): void {
  if (!root) return;
  if ((root as Element).classList?.contains(MARK_SPINNER_CLASS)) alignSpinnerAnimation(root as Element);
  const found = (root as ParentNode).querySelectorAll?.(`.${MARK_SPINNER_CLASS}`);
  if (!found) return;
  for (const el of Array.from(found)) alignSpinnerAnimation(el);
}

const installed = new WeakSet<Document>();

/** Install the one clock for `doc` (idempotent). Returns how to take it down again (for tests). */
export function installMarkSpinnerPhase(doc: Document | undefined = typeof document === 'undefined' ? undefined : document): () => void {
  if (!doc || typeof MutationObserver === 'undefined' || installed.has(doc)) return () => undefined;
  installed.add(doc);
  let observer: MutationObserver | null = null;
  const start = (): void => {
    if (!doc.body || observer) return;
    observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (node.nodeType !== 1) continue;
          const el = node as Element;
          if (el.classList.contains(MARK_SPINNER_CLASS) || el.getElementsByClassName(MARK_SPINNER_CLASS).length > 0) {
            alignMarkSpinners(el);
          }
        }
      }
    });
    observer.observe(doc.body, { childList: true, subtree: true });
    alignMarkSpinners(doc.body);
  };
  const onAnimationStart = (event: Event): void => {
    if ((event as AnimationEvent).animationName !== MARK_SPIN_ANIMATION) return;
    const target = event.target as Element | null;
    if (target && typeof (target as Element).classList !== 'undefined') alignSpinnerAnimation(target);
  };
  doc.addEventListener('animationstart', onAnimationStart, true);
  if (doc.body) start();
  else doc.addEventListener('DOMContentLoaded', start, { once: true });
  return () => {
    observer?.disconnect();
    observer = null;
    doc.removeEventListener('animationstart', onAnimationStart, true);
    doc.removeEventListener('DOMContentLoaded', start);
    installed.delete(doc);
  };
}
